import { Button } from "@/components/ui/button";

export interface ActionPreviewCardProps {
  summary: string;
  details: unknown;
  status: "pending" | "approved" | "rejected";
  pending: boolean;
  onApprove: () => void;
  onReject: () => void;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Narrow the loose preview details to a flat string-keyed record for display.
 * Raw ids are machine plumbing, not something a user can act on — the summary
 * already names the entity, so UUID-valued rows stay off the card (the full
 * payload remains inspectable via the tool chip).
 */
function detailEntries(details: unknown): [string, unknown][] {
  if (typeof details !== "object" || details === null) return [];
  return Object.entries(details as Record<string, unknown>).filter(
    ([, v]) => !(typeof v === "string" && UUID_RE.test(v)),
  );
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Inline confirm card for a Maxwell write. Renders the previewed summary and a
 * structured detail list; Approve commits via the commit endpoint, Reject ends
 * the action turn. Composes the shadcn `Button` primitive; the container follows
 * the transcript's token-based bubble idiom.
 */
export function ActionPreviewCard({
  summary,
  details,
  status,
  pending,
  onApprove,
  onReject,
}: ActionPreviewCardProps) {
  const entries = detailEntries(details);
  return (
    <div className="flex max-w-[85%] flex-col gap-3 self-start rounded-lg border border-l-[3px] border-border border-l-orange bg-card p-4">
      <p className="text-sm font-medium text-foreground">{summary}</p>
      {entries.length > 0 && (
        <dl className="flex flex-col gap-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 text-sm">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="text-right text-foreground">{renderValue(v)}</dd>
            </div>
          ))}
        </dl>
      )}
      {status === "pending" ? (
        <div className="flex gap-2">
          <Button type="button" size="sm" onClick={onApprove} disabled={pending}>
            {pending ? "Working…" : "Approve"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onReject}
            disabled={pending}
          >
            Reject
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {status === "approved" ? "Approved" : "Rejected"}
        </p>
      )}
    </div>
  );
}
